import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1097BTCInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1097-BTC instructions for Tax Year 2025"
          description="Learn IRS Form 1097-BTC step-by-step instructions for Tax Year 2025. Accurately report bond tax credit information with clear guidance from TaxZerone."
          keywords=""
          canonical="resources/information-returns/form-1097-btc-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight"> Form 1097-BTC</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Read the instructions and report the bond tax credit accurately with
          TaxZerone
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1097BTCinstructions`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1097-btc-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1097-BTC at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Form 1097-BTC, Bond Tax Credit
                        </strong>
                        , is used by issuers to report taxable credit bond tax
                        credit information to the IRS and to bond holders. The
                        form reports the tax credit amount associated with
                        certain taxable credit bonds issued under the Internal
                        Revenue Code.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        {'Recipient copies must be furnished by '}
                        <strong style={{ fontWeight: 600 }}>February 2</strong>
                        {', the IRS filing deadline is '}
                        <strong style={{ fontWeight: 600 }}>March 2</strong>
                        {' for paper filings and '}
                        <strong style={{ fontWeight: 600 }}>March 31</strong>
                        {' for electronic filings.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        {
                          'This article covers line-by-line instructions, including issuer and recipient information, '
                        }
                        <strong style={{ fontWeight: 600 }}>Box 1</strong>
                        {' (Bond Tax Credit)'}
                        {', '}
                        <strong style={{ fontWeight: 600 }}>Box 2a</strong>
                        {' (Code),'}{' '}
                        <strong style={{ fontWeight: 600 }}>Box 2</strong>
                        <strong style={{ fontWeight: 600 }}>b</strong>
                        <strong style={{ fontWeight: 600 }}> </strong>
                        (Unique Identifier)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>Box 3</strong>
                        (Bond'
                        <strong style={{ fontWeight: 600 }}> </strong>
                        Type ){', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 4 '}</strong>
                        (Reserved for Future Use)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>
                          {'Boxes 5a-5l '}
                        </strong>
                        (Monthly Credit Amounts) ,{' and '}
                        <strong style={{ fontWeight: 600 }}>{'Box 6 '}</strong>
                        (Comments) , along with other reporting requirements for
                        Form 1097-BTC.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Penalties:</strong>
                        {
                          ' Late, incorrect, or incomplete filing of Form 1097-BTC may result in IRS penalties ranging from '
                        }
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per return
                        </strong>
                        , depending on when the return is filed, whether
                        corrections are required, and whether the filer complies
                        with electronic filing requirements.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Extensions:</strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 8809
                          </strong>
                        </Link>
                        {' to request an automatic filing extension. '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 15397
                          </strong>
                        </Link>{' '}
                        {'to request the full '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>
                        {' for furnishing recipient copies'}
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization issues or transfers bond tax credits,
                  you’re required to report them using IRS Form 1097-BTC. This
                  form is used to report the allocation of tax credit bonds,
                  such as Build America Bonds or Qualified Energy Conservation
                  Bonds. Filing accurately and on time ensures proper credit
                  tracking and IRS compliance.{' '}
                </Typography>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#form" sx={{ color: '#0202ff' }}>
                          Form 1097-BTC
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Form 1097-BTC?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#types" sx={{ color: '#0202ff' }}>
                          Types of tax credit Bonds
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#dates" sx={{ color: '#0202ff' }}>
                          Important dates for filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#inst" sx={{ color: '#0202ff' }}>
                          Instructions to file Form 1097-BTC
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="form"
                >
                  Form 1097-BTC
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Form 1097-BTC, is an information form used to report bond tax
                  credits to the IRS annually and to bond holders or investors
                  on a quarterly basis. This form provides information including
                  CUSIP number, type of bond the bond holder has invested in,
                  the total credits and monthly credits. The bond holder can use
                  these credits while filing federal taxes.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="who"
                >
                  Who must file Form 1097-BTC?
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Bond Issuers or agents such as brokers, nominees, mutual
                  funds, or partnerships must file copy A of this form with the
                  IRS to report the{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1097-btc/"
                    sx={{ color: '#0202ff' }}
                  >
                    tax credits
                  </Link>{' '}
                  distributed. The issuers of bonds will send a copy B to the
                  bond holder if they receive a tax credit of at least $10
                  directly or indirectly from a tax credit bond or a stripped
                  credit coupon during the tax year.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="types"
                >
                  Types of tax credit bonds
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Credit distributed from the following tax credit bonds must be
                  reported on this form.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>New clean renewable energy bonds </li>
                  <li>Qualified energy conservation bonds </li>
                  <li>Qualified zone academy bonds </li>
                  <li>Qualified school construction bonds </li>
                  <li>Clean renewable energy bonds </li>
                  <li>Build America bonds (Tax Credit). </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="dates"
                >
                  Important dates for filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  The{' '}
                  <Link
                    href="/resources/information-returns/form-1097-btc-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 1097-BTC deadline
                  </Link>{' '}
                  varies depending on the filing method:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}> Paper filing:</span>{' '}
                    February 28{' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>Electronic filing:</span>{' '}
                    March 31{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Recipient copies must be furnished quarterly before the{' '}
                  <span style={{ fontWeight: 600 }}>15th day </span> of the{' '}
                  <span style={{ fontWeight: 600 }}>2nd month</span>.{' '}
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Quarter
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Reporting period
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Recipient deadline
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            1st Quarter
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            January to March
                          </TableCell>

                          <TableCell sx={{}}>May 15</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            2nd Quarter
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            April to June
                          </TableCell>

                          <TableCell sx={{}}>August 15</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            3rd Quarter
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            July to September
                          </TableCell>

                          <TableCell sx={{}}>November 15 </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Annual/4th Quarter
                          </TableCell>
                          <TableCell sx={{ borderRight: '1px solid #828282' }}>
                            October to December
                          </TableCell>

                          <TableCell sx={{}}>February 15 </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 2, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> If the deadline falls on the weekend or federal
                  holiday, the next business day is considered.
                </Alert>
                <Box
                  component="div"
                  sx={{ mb: 4, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        File with Taxzerone for a quick and easy way to submit
                        <br />
                        your IRS forms online.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1097BTCinstructions`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mt: 4,
                    mb: 1,
                    scrollMarginTop: 87,
                  }}
                  id="inst"
                >
                  Instructions to file Form 1097-BTC
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Follow the instructions and file your forms accurately with
                  Taxzerone.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1097-btc-foreign-postal-img.webp" />
                  <source srcSet="/assets/images/f1097-btc-foreign-postal-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1097-btc-foreign-postal-img.png"
                    loading="lazy"
                    alt="Instructions to file Form 1097-BTC"
                    width={525}
                    height={207}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the issuer’s full legal name, complete address including
                  City, State, country, ZIP code and telephone number.
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f1097-btc-legal-name-img.webp" />
                  <source srcSet="/assets/images/f1097-btc-legal-name-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1097-btc-legal-name-img.png"
                    loading="lazy"
                    alt="Enter the Issuer’s and Recipient’s TIN"
                    width={524}
                    height={78}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the Issuer’s and Recipient’s TIN (Taxpayer
                  Identification Number) in the appropriate field.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1097-btc-recipient-img.webp" />
                  <source srcSet="/assets/images/f1097-btc-recipient-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1097-btc-recipient-img.png"
                    loading="lazy"
                    alt=" Enter the Recipient's legal name"
                    width={525}
                    height={233}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the Recipient&apos;s legal name, address including
                  apartment number, city, state, country, ZIP code.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1097-btc-issuer-img.webp" />
                  <source srcSet="/assets/images/f1097-btc-issuer-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1097-btc-issuer-img.png"
                    loading="lazy"
                    alt="Select only one box that describes"
                    width={525}
                    height={181}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Select only one box that describes the issuer:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    Check the first box if you are the issuer of the bond or an
                    agent filing the current year’s Form 1097-BTC to report the
                    credit.{' '}
                  </li>
                  <li>
                    Check the second box if you are an entity or individual who
                    has received (or should have received) a current year Form
                    1097-BTC and you are now distributing part or all of that
                    credit to others.{' '}
                  </li>
                </Box>

                <picture>
                  <source srcSet="/assets/images/f1097-btc-calendar-img.webp" />
                  <source srcSet="/assets/images/f1097-btc-calendar-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1097-btc-calendar-img.png"
                    loading="lazy"
                    alt="Enter the calendar year"
                    width={217}
                    height={54}
                    className="img-responsive"
                    sx={{
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the calendar year to report when the bond tax credits
                  were issued.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1097-btc-total-img.webp" />
                  <source srcSet="/assets/images/f1097-btc-total-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1097-btc-total-img.png"
                    loading="lazy"
                    alt="Enter the total amount of credits"
                    width={217}
                    height={80}
                    className="img-responsive"
                    sx={{
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total amount of credits distributed to the recipient
                  for the calendar year, as reported in boxes 5a through 5l.
                  This field should be completed only when filing to the IRS
                  (Copy A) and providing the annual statement to the recipient
                  (Copy B).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2a
                </Typography>
                <Box>
                  <picture>
                    <source srcSet="/assets/images/f1097-btc-code-img.webp" />
                    <source srcSet="/assets/images/f1097-btc-code-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/f1097-btc-code-img.png"
                      loading="lazy"
                      alt="Enter the unique identification number code:"
                      width={217}
                      height={79}
                      className="img-responsive"
                      sx={{
                        border: '1px solid #333',
                        maxWidth: '100%',
                        mb: 2,
                      }}
                    />
                  </picture>
                </Box>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the unique identification number code:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    Enter <span style={{ fontWeight: 600 }}> “C”</span> for a
                    CUSIP number,{' '}
                  </li>
                  <li>
                    Enter <span style={{ fontWeight: 600 }}>“A”</span> for an
                    account number{' '}
                  </li>
                  <li>
                    Enter <span style={{ fontWeight: 600 }}>“O”</span> for
                    self-provided identification number{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you checked the first box under Form 1097-BTC issuer and
                  you don’t have a CUSIP number in bond, use the account number
                  or any other unique identifying number.{' '}
                </Typography>
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                      mb: 0.5,
                      fontWeight: 600,
                    }}
                  >
                    Box 2b
                  </Typography>
                  <picture>
                    <source srcSet="/assets/images/f1097-btc-unique-img.webp" />
                    <source srcSet="/assets/images/f1097-btc-unique-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/f1097-btc-unique-img.png"
                      loading="lazy"
                      alt="Enter the unique identification number code:"
                      width={432}
                      height={79}
                      className="img-responsive"
                      sx={{
                        border: '1px solid #333',
                        maxWidth: '100%',
                        mb: 2,
                      }}
                    />
                  </picture>
                </Box>

                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the Unique identification number which is assigned by
                  the issuer and the limit is up to 39 alphanumeric characters.
                  It can be CUSIP number, account number, or any other unique
                  identification number that is used to track bond transactions.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1097-btc-energy-img.webp" />
                  <source srcSet="/assets/images/f1097-btc-energy-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1097-btc-energy-img.png"
                    loading="lazy"
                    alt="For clean renewable energy bonds"
                    width={217}
                    height={79}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  For clean renewable energy bonds, enter code &quot;101&quot;
                  and for all other bond types, enter code &quot;199.&quot;
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 2, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> Multiple bond types can be reported on a single
                  Form 1097-BTC, except for clean renewable energy bonds. Bond
                  credits for clean renewable energy must be reported on a
                  separate 1097-BTC Form.
                </Alert>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1097-btc-box4-img.webp" />
                  <source srcSet="/assets/images/f1097-btc-box4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1097-btc-box4-img.png"
                    loading="lazy"
                    alt="Reserved for future use."
                    width={217}
                    height={79}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Reserved for future use.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Boxes 5a-5l
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1097-btc-boxes5-img.webp" />
                  <source srcSet="/assets/images/f1097-btc-boxes5-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1097-btc-boxes5-img.png"
                    loading="lazy"
                    alt="Boxes 5a-5l"
                    width={432}
                    height={309}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Report the monthly bond tax credits allowed to the recipient
                  for each month of the calendar year. Here’s how to calculate
                  the credit:{' '}
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  STEP 1:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  For Qualified tax credit bond, Clean renewable energy bond, or
                  Qualified zone academy bond (issued before October 4,
                  2008){' '}
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  calculate the credit by multiplying the outstanding face
                  amount of the bond by the applicable credit rate.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Outstanding face amount = Original face value – Principal
                  amount that has been repaid
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 600 }}>
                  Qualified Zone Academy Bonds (Issued Before July 1, 1999):
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Calculate the Credit rate by multiplying 110% (or 1.10) by the
                  long-term Applicable Federal Rate (AFR). AFR is published
                  monthly by the IRS in the Internal Revenue Bulletin.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 18, md: 17, sm: 15, xs: 15 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Build America Bond
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Calculate the Credit rate by multiplying 35% (or 0.35) by the
                  interest payable on the interest payment date. Enter the
                  amount in boxes 5a–5l for the month in which the interest
                  payment occurred.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 18, md: 17, sm: 15, xs: 15 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Example:
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the bond pays interest on June 30 and December 31, the
                  credit amounts should be entered in box 5f (June) and box 5l
                  (December).
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  STEP 2:
                </Typography>

                <Typography variant="body1" sx={{ mb: 1, fontWeight: 600 }}>
                  Qualified Energy Conservation Bond (Issued on March 15, 2017)
                </Typography>
                <Box component="div" sx={{ mb: 4 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Credit Allowance Date
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Send Recipient Copy
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Box on Form
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Credit Amount
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            March 15, 2025
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            May 15, 2025
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Box 5c
                          </TableCell>

                          <TableCell sx={{}}>
                            25% (0.25 × STEP 1 amount){' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            June 15, 2025
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            August 15, 2025
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Box 5f
                          </TableCell>

                          <TableCell sx={{}}>
                            25% (0.25 × STEP 1 amount){' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            September 15, 2025
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            November 17, 2025
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Box 5i
                          </TableCell>

                          <TableCell sx={{}}>
                            25% (0.25 × STEP 1 amount)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            December 15, 2025
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            February 16, 2026
                          </TableCell>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Box 5l
                          </TableCell>

                          <TableCell sx={{}}>
                            25% (0.25 × STEP 1 amount)
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 18, md: 17, sm: 15, xs: 15 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Clean renewable energy bond or Qualified tax credit bond
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the clean renewable energy bond or qualified tax credit
                  bond is issued, redeemed, or matured in the middle of 3-month
                  period, instead of getting the full 25% credit for that
                  quarter, credit will be prorated based on how many days the
                  bond was active during those 3 months.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 17, md: 16, sm: 14, xs: 14 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Example 2
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  A qualified zone academy bond that issued matures on March 23,
                  2025. The bond is not outstanding for the entire 3-month
                  period ending June 15, 2025, then you must prorate the 25% by
                  dividing the number of days the bond was active by total
                  number of days.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  Proration Calculation = 8 days (number of days from March 16
                  through March 23) / 92 days
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  (number of days from March 16 through June 15){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  = 0.087 x 0.25 = 2% (0.02)
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For the Second Quarter, the credit allowance date is June 15,
                  2025. Enter the amount in box 5f by multiplying 2% (0.02) with
                  STEP 1 amount. For annual reporting, enter in box 1{' '}
                  &quot;27%&quot; (0.27) of the amount determined in STEP
                  1.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f1097-btc-comment-img.webp" />
                  <source srcSet="/assets/images/f1097-btc-comment-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f1097-btc-comment-img.png"
                    loading="lazy"
                    alt="Enter any additional information. "
                    width={432}
                    height={79}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any additional information.
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information given is based on guidelines from the
                        IRS website. For more information, check the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i1097btc.pdf"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          IRS instructions for Form 1097-BTC
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1099gi"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          General Instructions for Information Returns
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to file Form 1097-BTC?
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              Report bond tax credits accurately and stay compliant with the IRS
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1097BTCinstructions`}
              >
                Start E-Filing Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1097BTCInstructions;
